'use strict';
// 3rd party libs
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  FC,
  ReactElement,
  useRef,
  useCallback
} from 'react';
import {
  ColumnSort,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel as defaultGetSortedRowModel,
  useReactTable,
  SortingState,
  Column,
  Row,
  CellContext,
  ColumnOrderState,
  Header,
  Table,
  getFilteredRowModel,
  getExpandedRowModel,
  BuiltInSortingFn,
  SortingFn,
  ExpandedState,
  RowData,
  ColumnResizeMode
} from '@tanstack/react-table';
import { QueryClient, QueryClientProvider, useInfiniteQuery } from '@tanstack/react-query';
import { useDrag, useDrop } from 'react-dnd';
import { default as theme } from 'themes';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faGripVertical,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import Filter from '../../icons/img/table-filter-icon.svg';
import ClearFilter from '../../icons/img/clear_filter.svg';
import camelCase from 'lodash/camelCase';
import { isEmpty, cloneDeep } from 'lodash';
import { useVirtual } from 'react-virtual';
import { BaseType, ModalSize, TableColumnConfigs } from 'types';
import {
  Typography,
  Modal,
  Checkbox,
  Button,
  StyledTable,
  StyledColumnRefContainer,
  StyledNonDraggableColumnRefContainer,
  StyledDragRefContainer,
  StyledTFoot,
  StyledAscDescContainer,
  OptionWrapper,
  ColumnOption,
  TableFilterOption,
  ImageOptionWrapper,
  ImageOption,
  OptionsModalContainer,
  SelectionHeader,
  OptionalColumnContainer,
  OptionColumnCheckboxWrapper,
  OptionalColumnName,
  SelectAllOptionalColumn,
  ModalButtonsContainer,
  StyledRootContainer,
  TooltipWrapper,
  DebounceInput,
  Loader,
  Wrapper,
  TableHeaderRule,
  TableCoverRule,
  ColumnOptionWrapper,
  SearchIconWrapper,
  TableButtonWrapper,
  InformationTooltip
} from 'components';
import { useTanStackFilterOptions } from 'selectors';
//column cofig
import { useTranslation } from 'react-i18next';
import { convertCamelCaseintoTitleCase, truncateText } from 'common/helpers/v1';
import styled, { CSSProperties } from 'styled-components';
import DropdownOption, { OptionType } from 'common/components/DropdownOption';
import { DataNotFound } from 'components/DataNotFound';
import { tanStackTableMultiFilterOption } from 'reducers/tanStackTable';
import AdvanceFilterPopUp, { AdvanceFilterOptionType } from 'common/components/AdvanceFilterPopUp';
import { IconSearch } from 'icons/indexed-icons/IconSearch';
import IcoTableColumnOption from 'icons/indexed-icons/IcoTableColumnOption';
import { BtnText, IconTextBtn, RibbonItem } from 'components/RibbonComponent';
import IntersectionObserverWrapper from './IntersectionObserverWrapper';
import { useMediaQuery } from 'react-responsive';
import breakpoints from 'constants/breakpoints';
import './responsive-table.css';
import { Mobile } from 'components';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import NestedTable from 'components/NestedTable';
import AsyncSearch, { SearchResult } from 'components/SearchInput/AsyncSearch';
import AdvanceStatusFilterPopUp from 'common/components/AdvanceStatusFilterPopUp';
import CustomLoader from 'components/CustomLoader';
import { useLocation } from 'react-router-dom';

type sortUndefinedType = -1 | 'first' | 'last' | 1 | false;

export interface ColumnConfig extends BaseType {
  id: string;
  header: string | ReactElement;
  isEnableSorting: boolean;
  isEnableResizing?: boolean;
  isSelected: boolean;
  renderer: (cellValue: CellContext<TableColumnConfigs, string>) => JSX.Element | React.ReactNode;
  customSort?: BuiltInSortingFn | SortingFn<TableColumnConfigs>;
  disableGlobalSearch?: boolean;
  sortUndefined?: sortUndefinedType;
}

interface SortStateItem extends BaseType {
  id: string;
  desc: boolean;
}

export type SortState = SortStateItem | SortStateItem[];
export interface NewBaseTableFetchParams {
  limit: number;
  offset: number;
  query: string;
  [key: string]: string | number | undefined | boolean;
}

export interface NewBaseTableFetchResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>[];
  total?: number;
}

export interface NewBaseTableCustomFetchFunctionProps {
  limit: number;
  offset: number;
  query?: string;
}

interface NewBaseTableProps {
  /** custom filter to use before table gets renderedd, will apply to data?.filter(filterData) */
  filterData?: (x?: Record<string, unknown>) => boolean;
  /** if you want to send a component to replace the built-in search */
  SearchComponent?: (x?: Record<string, unknown>) => JSX.Element;
  newTableData: TableColumnConfigs[];
  tableData?: TableColumnConfigs[];
  columnConfigs: ColumnConfig[];
  sortState: SortState | SortState[];
  enableFilter?: boolean;
  enableFilterOptionsSearch?: boolean;
  enableAdvanceFilter?: boolean;
  enableStatusFilter?: boolean;
  handleFilterRequest?: (val: string) => void;
  handleAdvanceFilterRequest?: (val: AdvanceFilterOptionType[]) => void;
  showSavedFiltersOption?: boolean;
  isShowColumnOptions?: boolean;
  isShowGlobalSearch?: boolean;
  isDataLoading?: boolean;
  removeColumnOption?: boolean;
  tdMarginLeft?: string;
  textAlign?: 'left' | 'center';
  padding?: string;
  tableHeadBtn?: JSX.Element;
  tableHeaderButtons?: RibbonItem[];
  shadow?: boolean;
  customClassName?: string;
  disableVirtualize?: boolean;
  hideTotalFetchCount?: boolean;
  maxTableHeight?: number;
  searchPlaceholder?: string;
  showTableCoverRule?: boolean;
  showTableHeaderRule?: boolean;
  searchInputValue?: (input?: string, searchKey?: string) => void;
  serverSideSearch?: boolean;
  getRowStyles?: (row: Row<TableColumnConfigs>) => CSSProperties;
  handleInlineEditing?: (formData?: Record<string, string | number | boolean>) => void;
  mobileViewRowHeader?: string | string[];
  truncateMobileViewRowHeader?: { colId: string; maxLength: number }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch?: LazyQueryTrigger<any>;
  enableServerSide?: boolean;
  ApiFetchSize?: number;
  columnSizingHeader?: boolean;
  customFetchFunction?: (params: NewBaseTableFetchParams) => Promise<NewBaseTableFetchResult>;
  customFetchFunctionURLParams?: (
    params: NewBaseTableFetchParams
  ) => Promise<NewBaseTableFetchResult>;
  /**
   * Opt-in for fully server-driven tables (with `enableServerSide` + `customFetchFunction`).
   * The value is added to the fetch query key (change it to reload from offset 0), the fetched pages
   * survive parent re-renders, and rows are not re-filtered or re-sorted on the client.
   */
  serverQueryKey?: unknown;
  /** Local rows (e.g. unsaved new rows) rendered above the server-fetched rows. */
  pinnedTopRows?: TableColumnConfigs[];
  tableCustomFilter?: number;
  selectedFilter?: number;
  //elastic search
  elasticSearch?: boolean;
  handleSearch?: (value: string | number) => void;
  handleSearchItemClick?: (value: SearchResult) => void;
  searchResult?: SearchResult[];
  inputValue?: string | number;
  isSearchLoading?: boolean;
  enableCopyLink?: boolean;
  isSearchFetching?: boolean;
  isCustomColumnHeaders?: boolean;
  addKeysToTableData?: (row: Record<string, any>) => { [key: string]: any };
  hasUrlFilters?: boolean;
  usePinnedBottom?: boolean;
  getSortedRowModelWithPinnedBottom?: () => any;
  hasNestedRows?: boolean;
  highlightRow?: (row: { [key: string]: unknown }) => boolean;
  emptyStateMessage?: string;
}

interface OptionalColumnsState {
  key: string;
  name: string;
  isSelected?: boolean;
  meta?: { [key: string]: boolean | string } | (() => { [key: string]: boolean | string });
}
type RowDataResponse = {
  data: TableColumnConfigs[];

  meta: {
    totalRowCount: number;
  };
};

interface GlobalSearchProps {
  isShowGlobalSearch?: boolean;
  globalFilter: string;
  searchInputValue?: (input?: string, searchKey?: string) => void | undefined;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
  searchPlaceholder?: string;
}
declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    getRowStyles: (row: Row<TData>) => CSSProperties;
  }
}

const { size } = breakpoints;

const keyPrefix = 'health-data';

// One spinner size for every loading state of server-managed tables (first load, sort/search, next page).
const SERVER_LOADER_SIZE = '3rem';
const SERVER_LOADER_THICKNESS = '3px';
const MIN_RELOAD_OVERLAY_HEIGHT = 160;
const columnHelper = createColumnHelper<TableColumnConfigs>();

const addKeysToData = (keyPrefix: string, data: TableColumnConfigs[]) => {
  return data.map((item) => {
    return {
      ...item,
      key: `${keyPrefix}-${item.id}`
    };
  });
};

const getTooltipState = (state: string) => {
  if (state.includes('service-tech')) {
    return {
      tooltipText: 'service tech search info'
    };
  }
  return '';
};

const reorderColumn = (
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  );
  return [...columnOrder];
};

const GlobalSearch = ({
  isShowGlobalSearch,
  globalFilter,
  searchInputValue,
  setGlobalFilter,
  searchPlaceholder
}: GlobalSearchProps) => {
  const { t } = useTranslation(['common']);
  const location = useLocation();
  const tooltipState = getTooltipState(location.pathname);
  return (
    <Wrapper data-targetid="global-search">
      {isShowGlobalSearch && (
        <OptionWrapper>
          <SearchIconWrapper>
            <IconSearch width="15" />
          </SearchIconWrapper>
          <DebounceInput
            value={globalFilter ?? ''}
            restrictSpaceFromStart={true}
            onChange={(value) => {
              searchInputValue?.(String(value), 'global');
              setGlobalFilter(String(value));
            }}
            placeholder={searchPlaceholder || (t('search') as string)}
            style={{
              paddingLeft: '1.4rem'
            }}
            className="globalSearchInput"
          />
          {tooltipState && (
            <InformationTooltip
              placement="left"
              tooltipText={t(tooltipState.tooltipText) as string}
              color="#0076cc"
              width="15rem"
              labelStyle={{ textAlign: 'center', display: 'inline-block' }}
            />
          )}
        </OptionWrapper>
      )}
    </Wrapper>
  );
};

const defaultSortIcons = (isSorted: string) => {
  return (
    {
      asc: (
        <StyledAscDescContainer>
          <div>
            <FontAwesomeIcon icon={faChevronUp} size="xs" />
          </div>
          <div>
            <FontAwesomeIcon icon={faChevronDown} style={{ visibility: 'hidden' }} size="xs" />
          </div>
        </StyledAscDescContainer>
      ),
      desc: (
        <StyledAscDescContainer>
          <div>
            <FontAwesomeIcon icon={faChevronDown} size="xs" />
          </div>
          <div>
            <FontAwesomeIcon icon={faChevronUp} style={{ visibility: 'hidden' }} size="xs" />
          </div>
        </StyledAscDescContainer>
      )
    }[isSorted] ?? (
      <StyledAscDescContainer>
        <div>
          <FontAwesomeIcon icon={faChevronUp} size="xs" />
        </div>
        <div>
          <FontAwesomeIcon icon={faChevronDown} size="xs" />
        </div>
      </StyledAscDescContainer>
    )
  );
};

const DraggableColumnHeader: FC<{
  header: Header<TableColumnConfigs, unknown>;
  table: Table<TableColumnConfigs>;
  tableColumns: OptionalColumnsState[];
  handleOptionChange: (name?: string, isSelected?: boolean) => void;
}> = ({ header, table, tableColumns, handleOptionChange }) => {
  const { getState, setColumnOrder } = table;
  const { columnOrder } = getState();
  const { column } = header;

  const [{ isOver }, dropRef] = useDrop({
    accept: 'column',
    drop: (draggedColumn: Column<TableColumnConfigs>) => {
      const newColumnOrder = reorderColumn(draggedColumn.id, column.id, columnOrder);
      setColumnOrder(newColumnOrder);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    item: () => column,
    type: 'column'
  });

  return (
    <StyledColumnRefContainer
      ref={dropRef}
      style={{
        opacity: isDragging ? 0 : 1
      }}
      key={header.id}
    >
      <div ref={previewRef}>
        {header.isPlaceholder ? null : (
          <StyledDragRefContainer ref={dragRef}>
            <OptionColumnCheckboxWrapper
              key={header.column.id}
              style={{
                backgroundColor: isOver ? 'lightgrey' : 'white'
              }}
            >
              <Checkbox
                key={header.column.id}
                id={`${header.column.id}-${header.index}`}
                width={20}
                height={20}
                checked={
                  tableColumns?.find((tableColumn) => tableColumn.key === column.id)?.isSelected
                }
                disabled={
                  tableColumns?.find((tableColumn) => tableColumn.key === column.id)?.meta
                    ?.isAlwaysVisible
                }
                onChange={() => {
                  const opt = tableColumns?.find((tableColumn) => tableColumn.key === column.id);
                  handleOptionChange(opt?.name, opt?.isSelected);
                }}
              />
              <OptionalColumnName>
                {convertCamelCaseintoTitleCase(header.column.id)}
              </OptionalColumnName>
              <FontAwesomeIcon
                icon={faGripVertical}
                color={theme.colors.darkBlue}
                style={{ marginLeft: 'auto', marginTop: 'auto', marginBottom: 'auto' }}
                size="xs"
              />
            </OptionColumnCheckboxWrapper>
          </StyledDragRefContainer>
        )}
      </div>
    </StyledColumnRefContainer>
  );
};

const TableContainer = styled.div`
  width: auto;
  margin-top: 0.5rem;

  overflow-anchor: none;
  scroll-behavior: smooth;

  @media (min-width: 2160px) {
    max-height: 1442rem;
  }

  tr {
    transition: all 0.2s ease-in-out;
  }

  &.scrolling {
    pointer-events: none;
  }
`;
export const FetchDetail = styled.div`
  color: #0076cc;
  margin-top: 12px;
  margin-bottom: -6px;
  padding-left: 10px;
`;

const VirtualRow = ({
  row,
  mobileViewRowHeader,
  truncateMobileViewRowHeader,
  getRowStyles,
  columnSizingHeader,
  highlightRow,
  openRowIds,
  setOpenRowIds,
  columnSizeVars,
  hasNestedRows
}: any) => {
  const isMobile = useMediaQuery({ maxWidth: size.md });

  const handleClick = useCallback(() => {
    setOpenRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  }, [row.id]);

  const isOpened = openRowIds.has(row.id);
  const rowClickHandler = isMobile ? { onClick: handleClick } : null;

  const rowHeaderCell = Array.isArray(mobileViewRowHeader)
    ? row.getVisibleCells().filter((cell) => mobileViewRowHeader.includes(cell.column.id))
    : [
        row.getVisibleCells().find((cell) => cell.column.id === mobileViewRowHeader) ||
          row.getVisibleCells()[0]
      ];

  const mobileViewCustomCellTypeContent = row
    .getVisibleCells()
    .filter((cell) => cell?.column?.columnDef?.mobileViewCustomCellType)
    .map((cell) => (
      <div
        key={cell.id}
        className="custom-cell-content"
        style={columnSizingHeader ? { ...columnSizeVars, width: cell.column.getTotalSize() } : {}}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    ));
  return row?.original?.newTable ? (
    <tr>
      <td colSpan={row.getVisibleCells().length}>
        {row?.original?.type === 'twowayorder' ||
        row?.original?.type === 'singleSessionStep' ||
        row?.original?.type === 'siptable' ? (
          <NestedTable type={row?.original?.type} data={row?.original?.newTable} />
        ) : (
          <></>
        )}
      </td>
    </tr>
  ) : (
    <tr
      key={row.id}
      className={`${row.subRows.length ? 'row' : 'row-hasChildren'} ${
        row.getIsExpanded() ? 'open' : ''
      } ${isOpened ? 'responsive-open' : ''} 
          ${row.depth === 1 ? 'level-2' : ''} ${highlightRow?.(row.original) ? "row-highlighted" : ""}`}
      style={typeof getRowStyles === 'function' ? getRowStyles(row) : {}}
      {...rowClickHandler}
    >
      <Mobile>
        {rowHeaderCell.length > 0 && (
          <td key={rowHeaderCell[0]?.id} className={rowHeaderCell[0]?.column?.id}>
            <div className="mobile-view-header-column">
              {!isOpened ? (
                <FontAwesomeIcon
                  icon={faChevronRight}
                  style={{
                    color: `${theme.colors.primaryBlue5}`,
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faChevronDown}
                  style={{ color: `${theme.colors.primaryBlue5}`, cursor: 'pointer' }}
                />
              )}
              {rowHeaderCell.map((cell) => {
                const cellContent = flexRender(cell?.column.columnDef.cell, cell?.getContext());
                const truncateConfig = truncateMobileViewRowHeader?.find(
                  (config) => config.colId === cell?.column.id
                );

                // Create modified context with truncated value
                const modifiedContext = truncateConfig
                  ? {
                      ...cell?.getContext(),
                      getValue: () => truncateText(cell?.getValue(), truncateConfig.maxLength)
                    }
                  : cell?.getContext();

                // Render cell with modified context
                const modifiedCellContent = truncateConfig
                  ? flexRender(cell?.column.columnDef.cell, modifiedContext)
                  : cellContent;

                return (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    key={cell?.id}
                  >
                    {modifiedCellContent}
                  </div>
                );
              })}
              {mobileViewCustomCellTypeContent}
            </div>
          </td>
        )}
      </Mobile>
      {row.getVisibleCells().map((cell) => {
        if (isMobile && cell.column.columnDef.mobileViewCustomCellType) {
          // Skip rendering this cell in its original place for mobile devices
          return null;
        }
        return (
          <td key={cell.id} className={cell.column.id}>
            {isMobile ? (
              <div className="mobile-view-data-column">
                <div className="mobile-view-data-column-title">
                  {typeof cell?.column?.columnDef?.header === 'function'
                    ? typeof cell?.column?.columnDef?.header() === 'string'
                      ? `${cell?.column?.columnDef?.header()}:`
                      : ''
                    : cell?.column?.columnDef?.header
                    ? `${cell?.column?.columnDef?.header}:`
                    : ''}
                </div>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </td>
        );
      })}
    </tr>
  );
};

function isAllColumnOptionsVisible(array: OptionalColumnsState[]) {
  // Iterate through each object in the array
  for (let i = 0; i < array.length; i++) {
    // Check if isSelected property is false
    if (!array[i].isSelected) {
      return false; // If any object's isSelected is false, return false
    }
  }
  return true; // If all objects' isSelected are true, return true
}

export const TanstackTable = ({ newTableData, ...props }: NewBaseTableProps): JSX.Element => {
  const filterOptions = useTanStackFilterOptions();
  const { t } = useTranslation(['common']);
  newTableData = props?.tableData || newTableData;
  const {
    columnConfigs,
    sortState,
    enableFilter,
    enableFilterOptionsSearch = true,
    enableAdvanceFilter,
    enableStatusFilter,
    handleFilterRequest,
    handleAdvanceFilterRequest,
    showSavedFiltersOption,
    isShowColumnOptions,
    isShowGlobalSearch,
    isDataLoading,
    removeColumnOption,
    tdMarginLeft,
    textAlign,
    padding,
    tableHeadBtn,
    tableHeaderButtons,
    shadow,
    customClassName,
    hideTotalFetchCount,
    disableVirtualize,
    maxTableHeight,
    searchPlaceholder,
    showTableCoverRule,
    showTableHeaderRule,
    searchInputValue,
    serverSideSearch,
    getRowStyles,
    handleInlineEditing,
    mobileViewRowHeader,
    truncateMobileViewRowHeader,
    refetch,
    enableServerSide,
    ApiFetchSize,
    columnSizingHeader,
    customFetchFunction,
    customFetchFunctionURLParams,
    serverQueryKey,
    pinnedTopRows,
    selectedFilter,
    // elastic search
    elasticSearch,
    handleSearchItemClick,
    searchResult,
    handleSearch,
    inputValue,
    isSearchFetching,
    isSearchLoading,
    enableCopyLink,
    isCustomColumnHeaders,
    addKeysToTableData,
    hasUrlFilters,
    usePinnedBottom,
    getSortedRowModelWithPinnedBottom,
    SearchComponent,
    hasNestedRows,
    highlightRow,
    emptyStateMessage
  } = props;

  const isMobile = useMediaQuery({ maxWidth: size.md });
  const isTablet = useMediaQuery({ minWidth: size.md + 1, maxWidth: size.xl });
  const isDesktopOrLaptop = useMediaQuery({ minWidth: size.xl + 1 });
  const location = useLocation();

  const [dataTable, setDataTable] = useState(() => (newTableData ? [...newTableData] : []));
  const [originalData, setOriginalData] = useState(() => (newTableData ? [...newTableData] : []));
  const [editedRows, setEditedRows] = useState({});

  const updatedSortState = Array.isArray(sortState) ? sortState : [sortState];
  const [sorting, setSorting] = useState<SortingState>(updatedSortState);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showColumnOptions, setShowColumnOptions] = useState<boolean>(false);
  const [tableColumns, setTableColumns] = useState<OptionalColumnsState[]>([]);
  const [tempTableColumns, setTempTableColumns] = useState<OptionalColumnsState[]>([]);
  const [selectAllOptionalColumns, setSelectAllOptionalColumns] = useState<boolean>(
    tableColumns.length !== 0 && isAllColumnOptionsVisible(tableColumns)
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const [showFilter, setShowFilter] = useState<boolean>(enableFilter as boolean);
  const [showAdvanceFilter, setShowAdvanceFilter] = useState<boolean>(
    enableAdvanceFilter as boolean
  );
  const [showStatusFilter, setStatusFilter] = useState<boolean>(enableStatusFilter as boolean);
  const [showClearFilter, setShowClearFilter] = useState<boolean>(false);
  const [showClearAdvanceFilter, setShowClearAdvanceFilter] = useState<boolean>(false);

  const [globalFilter, setGlobalFilter] = React.useState('');
  const [additionalFilter, setAdditionalFilter] = React.useState('');
  const [columnResizeMode] = React.useState<ColumnResizeMode>('onChange');
  const [resizeDirection] = useState<'ltr' | 'rtl'>('ltr');

  const [openRowIds, setOpenRowIds] = useState(new Set<string>());

  /*
   * Adding this function for getting searchable
   * columns from columnconfig.
   * by default global search works on all columns
   * if you want to remove global search from specific column
   * you have to pass key `disableGlobalSearch = true`
   */

  const searchableCol = useMemo(() => {
    return columnConfigs
      ?.filter((columnConfig) => {
        return !columnConfig.disableGlobalSearch;
      })
      .map((data) => data.id);
  }, []);

  let columns = columnConfigs?.map((columnConfig) => {
    const metaData = {};
    //Backward compatibility for old column configs
    metaData['isAlwaysVisible'] = columnConfig?.isAlwaysVisible || false;
    metaData['isEnableResizing'] = columnConfig?.isEnableResizing || false;
    //Dynamicly set keys and values that are stores in meta object
    for (const [key, value] of Object.entries(columnConfig.meta || {})) {
      metaData[key] = value;
    }

    return columnHelper.accessor(columnConfig?.id, {
      id: columnConfig.id,
      header: () => columnConfig.header,
      cell: (info: CellContext<TableColumnConfigs, string> | JSX.Element) => {
        if (typeof columnConfig.cell === 'function') {
          const Cell = columnConfig.cell;
          return <Cell {...info} />;
        } else {
          return <span className="cell">{columnConfig.renderer(info)}</span>;
        }
      },
      enableSorting: columnConfig.isEnableSorting,
      sortUndefined: columnConfig.sortUndefined,
      meta: metaData,
      enableGlobalFilter: searchableCol.includes(columnConfig.id),
      sortingFn: columnConfig.customSort || 'basic',
      ...(columnConfig.mobileViewCustomCellType
        ? { mobileViewCustomCellType: columnConfig.mobileViewCustomCellType }
        : {})
    });
  });

  const TotalFetchSize = enableServerSide && ApiFetchSize ? ApiFetchSize : 20;

  const fetchSize = enableServerSide || !disableVirtualize ? TotalFetchSize : newTableData?.length;
  const isServerManaged = Boolean(enableServerSide && serverQueryKey !== undefined);

  useEffect(() => {
    columns.forEach((opt) => {
      const targetCol = columnConfigs.find((colConfig) => colConfig?.id === opt?.id);
      tableColumns.push({
        key: camelCase(opt?.id),
        name: opt?.id || '',
        isSelected: targetCol?.isSelected,
        meta: { ...opt?.meta }
      });
    });

    setSelectAllOptionalColumns(isAllColumnOptionsVisible(tableColumns));
  }, []);

  useEffect(() => {
    setColumnOrder(columns.map((col) => col?.id || ''));
  }, []);

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columns?.map((column) => column?.id as string)
  );

  const tableData = useMemo(() => {
    if (props?.tableData) return props.tableData as typeof newTableData;
    if (!newTableData?.length) return [];

    return addKeysToData(keyPrefix, newTableData);
  }, [newTableData]);

  const triggerShowColumnOptions = (): void => {
    const newTableColumns = cloneDeep(tableColumns);
    const activeColumns: OptionalColumnsState[] = [];
    newTableColumns.forEach((col) => {
      if (col.key !== 'isDummy' && col.key !== 'action') activeColumns.push(col);
    });
    setTempTableColumns(newTableColumns);
    setTableColumns(activeColumns);
    setShowColumnOptions(true);
  };

  const onCloseCommentModal = () => {
    /* eslint-disable  @typescript-eslint/no-non-null-assertion */
    setTableColumns(tempTableColumns);
    setShowColumnOptions!(false);
  };

  const handleOptionChange = (name?: string, isSelected?: boolean) => {
    tableColumns.forEach((col) => {
      if (col.name === name) {
        col.isSelected = !isSelected;
      }
    });
    setSelectAllOptionalColumns(isAllColumnOptionsVisible(tableColumns));
    setTableColumns([...tableColumns]);
  };

  const handleSelectAllOptionsChange = () => {
    tableColumns
      .filter((col) => col.meta?.isAlwaysVisible === false)
      .forEach((col) => {
        col.isSelected = !selectAllOptionalColumns;
      });
    setTableColumns([...tableColumns]);
    setSelectAllOptionalColumns(isAllColumnOptionsVisible(tableColumns));
  };

  if (!isEmpty(tableColumns)) {
    columns = columns.filter((column) => {
      const compareItem = tableColumns.find(
        (tableColumn) => tableColumn.key === camelCase(column.id)
      );
      return compareItem && compareItem.isSelected;
    });
  }
  // get row data
  const fetchData = async (
    start: number,
    size: number,
    sorting: SortingState
  ): Promise<{
    data: TableColumnConfigs[];
    meta: {
      totalRowCount: number;
    };
  }> => {
    const dbData = [...tableData];
    const sort = sorting[0] as ColumnSort | undefined;
    const { id, desc } = (sort ?? {}) as { id?: string; desc?: boolean };

    if (id) {
      dbData.sort((a, b) => {
        if (desc) {
          return String(b[id]).localeCompare(String(a[id]), undefined, { caseFirst: 'lower' });
        }
        return String(a[id]).localeCompare(String(b[id]), undefined, { caseFirst: 'lower' });
      });
    }

    const fetchFunction = customFetchFunction || customFetchFunctionURLParams;
    if (enableServerSide && fetchFunction) {
      try {
        const response = await fetchFunction({
          limit: fetchSize,
          offset: start,
          query: globalFilter,
          searchParams: hasUrlFilters && location?.search,
          // Server-managed tables sort on the server; other tables keep the previous params.
          ...(isServerManaged && id ? { sortBy: id, sortOrder: desc ? 'desc' : 'asc' } : {})
        });

        // Add unique IDs to each row based on the page offset
        const dataWithUniqueIds = (response?.data || []).map((row, index) => ({
          ...row,
          uniqueId: `${start + index}-${row.id || index}`,
          ...(addKeysToTableData && addKeysToTableData(row))
        }));

        return {
          data: dataWithUniqueIds,
          meta: {
            totalRowCount: Number(response?.total)
          }
        };
      } catch (e) {
        console.log(e);
        console.error('Error fetching data from customFetchFunction');
      }
    }

    /*** Function for calling API after scroll reached end of table */
    if (enableServerSide && refetch) {
      const { data } = await refetch({
        limit: fetchSize,
        offset: start,
        search: globalFilter
      });
      return {
        data: data?.prosealStatuses || [],
        meta: {
          totalRowCount: Number(data?.totalCount)
        }
      };
    }

    return {
      data: dbData.slice(start, start + size),
      meta: {
        totalRowCount: dbData?.length
      }
    };
  };
  //react-query has an useInfiniteQuery hook just for this situation!
  const { data, fetchNextPage, isFetching, isFetchingNextPage, isInitialLoading } =
    useInfiniteQuery<RowDataResponse>(
      [
        'table-data',
        sorting,
        globalFilter,
        selectedFilter,
        enableServerSide || customFetchFunction
          ? customFetchFunctionURLParams
            ? newTableData
            : undefined
          : newTableData,
        serverSideSearch,
        additionalFilter,
        hasUrlFilters && location?.search,
        serverQueryKey
      ], //adding sorting state as key causes table to reset and fetch from new beginning upon sort
      async ({ pageParam = 0 }) => {
        const start = pageParam * fetchSize;
        if (sorting.length === 0) {
          const fetchedData = fetchData(start, fetchSize, updatedSortState); //pretend api call
          return fetchedData;
        } else {
          const fetchedData = fetchData(start, fetchSize, sorting); //pretend api call
          return fetchedData;
        }
      },
      {
        getNextPageParam: (_lastGroup, groups) => groups.length,
        keepPreviousData: true,
        refetchOnWindowFocus: false
      }
    );

  //we must flatten the array of arrays from the useInfiniteQuery hook
  const flatData = useMemo(() => {
    const fetched = data?.pages?.flatMap((page) => page.data) ?? [];
    const flat = pinnedTopRows?.length ? [...pinnedTopRows, ...fetched] : fetched;
    if (props?.filterData) return flat?.filter(props.filterData);
    else return flat;
  }, [data, pinnedTopRows]);

  // Server-managed table loading from offset 0 (first load / sort / search / refresh).
  const isServerReloading = isServerManaged && isFetching && !isFetchingNextPage;
  // Rows-area box for the reload overlay, measured before paint so the spinner is centred on first open too.
  const [reloadOverlayBox, setReloadOverlayBox] = useState<{ top: number; height: number } | null>(
    null
  );
  useLayoutEffect(() => {
    const container = tableContainerRef.current;
    if (isServerReloading && container) {
      setReloadOverlayBox({
        top: container.offsetTop,
        height: Math.max(container.clientHeight, MIN_RELOAD_OVERLAY_HEIGHT)
      });
    }
  }, [isServerReloading]);

  // New sort/search/refresh restarts at offset 0, so show the list from the top.
  const serverQueryKeyHash = isServerManaged ? JSON.stringify(serverQueryKey) : '';
  useEffect(() => {
    if (isServerManaged && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [isServerManaged, sorting, globalFilter, serverQueryKeyHash]);

  const totalDBRowCount = data?.pages?.[0]?.meta?.totalRowCount ?? 0;
  const totalFetched = flatData.length - (pinnedTopRows?.length ?? 0);
  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        const onScrollReach = enableServerSide ? 1000 : 300;
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < onScrollReach &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  );

  //a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const sortedRowModelFactory = usePinnedBottom
    ? getSortedRowModelWithPinnedBottom(defaultGetSortedRowModel)
    : defaultGetSortedRowModel();

  const table = useReactTable({
    data: flatData,
    columns: isCustomColumnHeaders ? columnConfigs : columns,
    defaultColumn: {
      size: 110
    },
    columnResizeMode: 'onChange',
    // Server already searched and ordered the rows; don't filter/sort the loaded pages again.
    manualFiltering: isServerManaged,
    manualSorting: isServerManaged,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      globalFilter,
      expanded
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: sortedRowModelFactory,
    onColumnVisibilityChange: setColumnVisibility,
    meta: {
      editedRows,
      setEditedRows,
      revertCellData: (rowIndex: number, revert: boolean) => {
        if (revert) {
          setDataTable((old) =>
            old.map((row, index) => (index === rowIndex ? originalData[rowIndex] : row))
          );
        } else {
          setOriginalData((old) =>
            old.map((row, index) => (index === rowIndex ? dataTable[rowIndex] : row))
          );
        }
      },
      updateRow: (data: Record<string, unknown>) => {
        handleInlineEditing && handleInlineEditing(data);
      }
    }
    //debugTable: true,
    //debugHeaders: true,
    //debugColumns: true
  });
  const handleFilterOptionChange = (val: string): void => {
    /* eslint-disable  @typescript-eslint/no-non-null-assertion */
    handleFilterRequest!(val);
    setAdditionalFilter(val);
    setShowFilter(!showFilter);
    setShowClearFilter(true);
  };

  const handleAdvanceFilterOptionChange = (val: AdvanceFilterOptionType[]): void => {
    /* eslint-disable  @typescript-eslint/no-non-null-assertion */
    handleAdvanceFilterRequest!(val);
  };

  const handleFilterSelect = (option?: OptionType) => {
    handleFilterOptionChange(option?.value || '');
  };

  const handleAdvanceFilterSelect = (option: AdvanceFilterOptionType[]) => {
    handleAdvanceFilterOptionChange(option);
  };

  const handleClearFilterClick = () => {
    setShowFilter(!showFilter);
    setShowClearFilter(false);
    setAdditionalFilter('');
    /* eslint-disable  @typescript-eslint/no-non-null-assertion */
    handleFilterRequest!('All');
  };

  const handleClearAdvanceFilterClick = () => {
    setShowAdvanceFilter(!showAdvanceFilter);
    setStatusFilter(!showStatusFilter);
    setShowClearAdvanceFilter(false);
    /* eslint-disable  @typescript-eslint/no-non-null-assertion */
    handleAdvanceFilterRequest!([]);
  };
  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const useCoreRowModel =
    ['userTable', 'parts-table'].includes(customClassName || '') && !hasNestedRows;
  const { rows } = useCoreRowModel ? table.getCoreRowModel() : table.getRowModel();

  //Virtualizing is optional, but might be necessary if we are going to potentially have hundreds or thousands of rows
  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: !disableVirtualize ? 10 : rows.length
  });
  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const tableHeaderButtonsMarkup = useMemo(() => {
    return tableHeaderButtons
      ? tableHeaderButtons.map((ribbon) => {
          const { icon: Icon, disable, textColor } = ribbon || {};
          return (
            <IconTextBtn
              gap=".5rem"
              disable={disable}
              onClick={ribbon?.onClick}
              data-targetid={ribbon?.label}
              className="table-header-button"
            >
              {Icon && <Icon width={24} {...(disable ? { color: theme.colors.lightGrey9 } : {})} />}
              <BtnText color={textColor ? textColor : ''}>{ribbon?.label}</BtnText>
            </IconTextBtn>
          );
        })
      : [];
  }, [tableHeaderButtons]);

  return (
    <StyledRootContainer
      className={customClassName}
      style={isServerManaged ? { position: 'relative' } : undefined}
    >
      {showTableCoverRule && <TableCoverRule />}
      {removeColumnOption ? null : (
        <>
          <ColumnOption shadow={shadow} className="customcss">
            <IntersectionObserverWrapper>
              <TableButtonWrapper data-targetid="button-section">
                {tableHeadBtn}
                {tableHeaderButtonsMarkup}
              </TableButtonWrapper>
              <OptionWrapper data-targetid="filter-section">
                <TableFilterOption>
                  {showFilter && (
                    <DropdownOption
                      searchable={enableFilterOptionsSearch}
                      icon={Filter}
                      handleOptionSelect={handleFilterSelect}
                      options={filterOptions as { value: string; label: string }[]}
                      searchInputValue={searchInputValue}
                    />
                  )}
                  {showClearFilter && (
                    <ImageOptionWrapper>
                      <TooltipWrapper Tooltip="Clear Filter" left>
                        <ImageOption
                          src={ClearFilter}
                          onClick={handleClearFilterClick}
                          // marginTop="-0.25rem"
                          // marginLeft="-0.1rem"
                        />
                      </TooltipWrapper>
                    </ImageOptionWrapper>
                  )}

                  {showAdvanceFilter && (
                    <AdvanceFilterPopUp
                      showSavedFiltersOption={showSavedFiltersOption}
                      handleOptionSelect={handleAdvanceFilterSelect}
                      options={filterOptions as tanStackTableMultiFilterOption[]}
                      searchInputValue={searchInputValue}
                    />
                  )}
                  {showStatusFilter && (
                    <AdvanceStatusFilterPopUp
                      handleOptionSelect={handleAdvanceFilterSelect}
                      options={filterOptions as tanStackTableMultiFilterOption[]}
                    />
                  )}
                  {showClearAdvanceFilter && (
                    <ImageOptionWrapper>
                      <TooltipWrapper Tooltip="Clear Filter">
                        <ImageOption
                          src={ClearFilter}
                          onClick={handleClearAdvanceFilterClick}
                          marginTop="-0.25rem"
                          marginLeft="-0.1rem"
                        />
                      </TooltipWrapper>
                    </ImageOptionWrapper>
                  )}
                </TableFilterOption>
              </OptionWrapper>
              <Wrapper data-targetid="column-options">
                {isShowColumnOptions && (
                  <OptionWrapper
                    onClick={() => {
                      triggerShowColumnOptions!();
                    }}
                  >
                    <ColumnOptionWrapper className="table-header-button">
                      <IcoTableColumnOption width="16" />
                      <span>
                        {!isDesktopOrLaptop ? (
                          <>{t('Column options')}</>
                        ) : (
                          <>
                            {t('Column options')} (
                            {
                              table
                                .getHeaderGroups()[0]
                                .headers.filter((head) => head.id !== 'action').length
                            }{' '}
                            {t('Columns')})
                          </>
                        )}
                      </span>
                    </ColumnOptionWrapper>
                  </OptionWrapper>
                )}
              </Wrapper>
              {SearchComponent ? (
                <SearchComponent />
              ) : (
                <>
                  {isDesktopOrLaptop ||
                  ((isMobile || isTablet) && !tableHeaderButtons && !tableHeadBtn) ? (
                    <>
                      <GlobalSearch
                        isShowGlobalSearch={isShowGlobalSearch}
                        globalFilter={globalFilter}
                        searchInputValue={searchInputValue}
                        setGlobalFilter={setGlobalFilter}
                        searchPlaceholder={searchPlaceholder}
                      />
                      {elasticSearch === true && (
                        <>
                          <AsyncSearch
                            handleClick={handleSearchItemClick}
                            searchResult={searchResult}
                            handleSearch={handleSearch}
                            inputValue={inputValue ?? ''}
                            isSearching={(isSearchLoading ?? false) || (isSearchFetching ?? false)}
                            enableCopyLink={enableCopyLink ?? false}
                          />
                        </>
                      )}
                    </>
                  ) : null}
                </>
              )}
            </IntersectionObserverWrapper>
          </ColumnOption>
          {(isMobile || isTablet) && (tableHeaderButtons || tableHeadBtn) ? (
            <ColumnOption className="customcss global-search-mobile-view">
              <GlobalSearch
                isShowGlobalSearch={isShowGlobalSearch}
                globalFilter={globalFilter}
                searchInputValue={searchInputValue}
                setGlobalFilter={setGlobalFilter}
                searchPlaceholder={searchPlaceholder}
              />
            </ColumnOption>
          ) : null}
        </>
      )}
      {showTableHeaderRule && <TableHeaderRule />}
      <Modal
        onClose={onCloseCommentModal}
        size={ModalSize.XXSMALL_AUTO_HEIGHT}
        title="Column Options"
        visible={showColumnOptions as boolean}
        widthOverride="25rem"
      >
        <OptionsModalContainer>
          <SelectionHeader>
            <Typography variant="body2" color={theme.colors.darkGrey}>
              Select columns to show
            </Typography>
          </SelectionHeader>

          <OptionalColumnContainer>
            {table.getHeaderGroups().map((headerGroup) => (
              <div key={headerGroup.id}>
                {headerGroup.headers.map(
                  (header) =>
                    header.id !== 'action' && (
                      <DraggableColumnHeader
                        key={header.id}
                        header={header}
                        table={table}
                        tableColumns={tableColumns}
                        handleOptionChange={handleOptionChange}
                      />
                    )
                )}
              </div>
            ))}

            {tableColumns.map((opt, index) => {
              const headerGroup = table.getHeaderGroups()[0];
              const headers = headerGroup?.headers;
              const header = headers?.find((header) => header.id === opt.name);

              return header ? (
                ''
              ) : (
                <StyledNonDraggableColumnRefContainer key={index}>
                  <OptionColumnCheckboxWrapper>
                    <Checkbox
                      key={index}
                      id={`${opt.name}-${index}`}
                      width={20}
                      height={20}
                      checked={opt.isSelected}
                      onChange={() => {
                        handleOptionChange(opt.name, opt.isSelected);
                      }}
                    />
                    <OptionalColumnName>
                      {convertCamelCaseintoTitleCase(t(opt.name))}
                    </OptionalColumnName>
                  </OptionColumnCheckboxWrapper>
                </StyledNonDraggableColumnRefContainer>
              );
            })}
          </OptionalColumnContainer>
          <SelectAllOptionalColumn>
            <Checkbox
              id={`opt-all`}
              width={20}
              height={20}
              checked={selectAllOptionalColumns}
              onChange={() => {
                handleSelectAllOptionsChange();
              }}
            />
            <OptionalColumnName htmlFor="checkbox-opt-all">Select All</OptionalColumnName>
          </SelectAllOptionalColumn>
        </OptionsModalContainer>
        <ModalButtonsContainer>
          <Button onClick={onCloseCommentModal} variant="filled-primary" size="small">
            Close
          </Button>
        </ModalButtonsContainer>
      </Modal>
      <TableContainer
        className="table-container-wrapper"
        // className={isScrolling ? 'scrolling' : ''}
        style={
          !disableVirtualize
            ? {
                maxHeight: maxTableHeight ? `${maxTableHeight}vh` : '100vh',
                overflow: 'auto',
                width: columnSizingHeader ? table.getCenterTotalSize() : 'auto'
              }
            : { maxHeight: '100%', overflow: 'auto' }
        }
        onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        ref={tableContainerRef}
        aria-busy={isServerReloading}
      >
        <StyledTable
          padding={padding}
          textAlign={textAlign}
          tdMarginLeft={tdMarginLeft}
          {...(isMobile && { className: 'rtr-table' })}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const enableResizing = header.column.columnDef.meta?.isEnableResizing;
                  return (
                    <th
                      key={header.id}
                      className={header.id}
                      colSpan={header.colSpan}
                      style={columnSizingHeader ? { width: header.getSize() } : {}}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : 'disabled-sort'
                          }}
                          style={{
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {(header.column.columnDef.enableSorting ||
                            header.column.columnDef?.isEnableSorting) && (
                            <span onClick={header.column.getToggleSortingHandler()}>
                              {defaultSortIcons(header.column.getIsSorted() as string)}
                            </span>
                          )}
                          {/* column resizing */}
                          {columnSizingHeader && enableResizing ? (
                            <div
                              {...{
                                onDoubleClick: () => header.column.resetSize(),
                                onMouseDown: header.getResizeHandler(),
                                onTouchStart: header.getResizeHandler(),
                                className: `resizer ${resizeDirection} ${
                                  header.column.getIsResizing() ? 'isResizing' : ''
                                }`,
                                style: {
                                  transform:
                                    columnResizeMode === 'onEnd' && header.column.getIsResizing()
                                      ? `translateX(${
                                          (resizeDirection === 'rtl' ? -1 : 1) *
                                          (table.getState().columnSizingInfo.deltaOffset ?? 0)
                                        }px)`
                                      : ''
                                }
                              }}
                            />
                          ) : null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <p style={{ visibility: 'hidden', height: '0', margin: '0' }}>
            {isFetching && !disableVirtualize
              ? (document.body.style.overflow = 'hidden')
              : (document.body.style.overflow = 'auto')}
          </p>
          {isDataLoading ? (
            <Loader margin={'2rem 0rem'} size={40} />
          ) : (
            <tbody>
              {paddingTop > 0 ? (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              ) : null}
              {isInitialLoading && !isServerManaged ? (
                <tr
                  style={{
                    width: '85%',
                    wordBreak: 'break-all'
                  }}
                >
                  <td
                    colSpan={999}
                    style={{
                      wordBreak: 'break-all',
                      marginTop: '.5rem',
                      fontSize: '1.2rem',
                      border: 'none'
                    }}
                  >
                    <CustomLoader size="4rem" />
                  </td>
                </tr>
              ) : null}
              {virtualRows.length === 0 && !isInitialLoading ? (
                <tr
                  style={{
                    width: '85%',
                    wordBreak: 'break-all'
                  }}
                >
                  <td
                    colSpan={999}
                    style={{
                      wordBreak: 'break-all',
                      marginTop: '.5rem',
                      fontSize: '1.2rem',
                      border: 'none'
                    }}
                  >
                    <DataNotFound textMsg={emptyStateMessage || (t('nothing_to_show') as string) || ''} />
                  </td>
                </tr>
              ) : null}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index] as Row<TableColumnConfigs>;
                return (
                  <VirtualRow
                    key={row.id}
                    row={row}
                    mobileViewRowHeader={mobileViewRowHeader}
                    truncateMobileViewRowHeader={truncateMobileViewRowHeader}
                    getRowStyles={getRowStyles}
                    columnSizingHeader={columnSizingHeader}
                    openRowIds={openRowIds}
                    setOpenRowIds={setOpenRowIds}
                    columnSizeVars={columnSizeVars}
                    hasNestedRows={hasNestedRows}
                    highlightRow={highlightRow}
                  />
                );
              })}
              {isFetchingNextPage ? (
                <tr>
                  <td colSpan={999}>
                    {isServerManaged ? (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CustomLoader size={SERVER_LOADER_SIZE} thickness={SERVER_LOADER_THICKNESS} />
                      </div>
                    ) : (
                      <CustomLoader size="3rem" thickness="3px" />
                    )}
                  </td>
                </tr>
              ) : null}
              {paddingBottom > 0 ? (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              ) : null}
            </tbody>
          )}

          <StyledTFoot>
            {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.footer, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </StyledTFoot>
        </StyledTable>
      </TableContainer>
      {isServerReloading && reloadOverlayBox && (
        // Covers only the rows area (search box and toolbar stay usable) and blocks clicks until the new page arrives.
        <div
          className="table-refetch-overlay"
          data-testid="table-refetch-overlay"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: reloadOverlayBox.top,
            height: reloadOverlayBox.height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 2
          }}
        >
          <CustomLoader size={SERVER_LOADER_SIZE} thickness={SERVER_LOADER_THICKNESS} />
        </div>
      )}
      {virtualRows.length !== 0 && (
        <FetchDetail className="footer-total-count">
          {!hideTotalFetchCount
            ? globalFilter.length > 0
              ? enableServerSide
                ? `Showing ${flatData.length} of ${totalDBRowCount} Rows`
                : `Showing ${rows.length} of ${totalDBRowCount} Rows`
              : `Showing ${flatData.length} of ${totalDBRowCount} Rows`
            : null}
        </FetchDetail>
      )}
    </StyledRootContainer>
  );
};
const NewBaseTable = (props: NewBaseTableProps): JSX.Element => {
  const [stableQueryClient] = useState(() => new QueryClient());
  // Existing tables rely on a fresh client per render to refetch; server-managed tables keep their pages.
  const queryClient = props.serverQueryKey !== undefined ? stableQueryClient : new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <TanstackTable {...props} />
    </QueryClientProvider>
  );
};

export default NewBaseTable;
